import { LitElement, html, css, nothing } from "lit";
import { customElement, state } from "lit/decorators.js";
import "../components/index.js";
import { toast, type BnColumn, type BnRow } from "../components/index.js";
import { humanError } from "../api/errors.js";
import {
  getSettings,
  searchTeams,
  listTeamLimits,
  upsertTeamLimits,
  deleteTeamLimits,
  type AdminTeam,
  type GlobalLimits,
  type TeamLimitRow,
  type TeamLimitOverrides,
} from "../api/admin.js";
import {
  adminTabs,
  adminTabsStyles,
  mibToBytes,
  bytesToMib,
  minutesToSeconds,
  secondsToMinutes,
} from "./admin-shared.js";

const SEARCH_DEBOUNCE_MS = 300;

/**
 * The ten overridable dimensions, each with its wire key, its display/parse
 * conversion (UI speaks cores, MiB, and minutes; the wire bytes and seconds),
 * and a unit suffix. One descriptor drives the table cells, the modal inputs,
 * and the inherit tooltips, so the dimensions can never disagree on units —
 * and so the PUT, which replaces the whole override set, can never drop one.
 */
interface Dim {
  key: keyof TeamLimitOverrides;
  label: string;
  /** Wire value -> display number. */
  toUi: (n: number) => number;
  /** Display number -> wire value. */
  toWire: (n: number) => number;
  unit: string;
  /**
   * Set on the repository limits and the mail budget, where the form also
   * takes 0 for inherit and a global 0 is a value (the built-in cap or
   * budget, no quota) that fmt spells.
   */
  zeroInherits?: boolean;
  fmt?: (n: number) => string;
}

/** show renders a wire value with its unit, or as the dimension spells it. */
function show(dim: Dim, n: number): string {
  return dim.fmt ? dim.fmt(n) : `${dim.toUi(n)}${dim.unit}`;
}

const DIMS: Dim[] = [
  { key: "cpuCores", label: "CPU cores", toUi: (n) => n, toWire: (n) => n, unit: "" },
  { key: "memoryBytes", label: "Memory", toUi: bytesToMib, toWire: mibToBytes, unit: " MiB" },
  { key: "pidsLimit", label: "Processes", toUi: (n) => n, toWire: Math.round, unit: "" },
  {
    key: "idleTimeoutSeconds",
    label: "Idle timeout",
    toUi: secondsToMinutes,
    toWire: minutesToSeconds,
    unit: " min",
  },
  {
    key: "wallClockTimeoutSeconds",
    label: "Max lifetime",
    toUi: secondsToMinutes,
    toWire: minutesToSeconds,
    unit: " min",
  },
  {
    key: "liveIdleTimeoutFloorSeconds",
    label: "Live idle floor",
    toUi: secondsToMinutes,
    toWire: minutesToSeconds,
    unit: " min",
  },
  { key: "liveMaxAlwaysOn", label: "Always-on live", toUi: (n) => n, toWire: Math.round, unit: "" },
  {
    key: "repositoryMaxBranches",
    label: "Branch workspaces per repository",
    toUi: (n) => n,
    toWire: Math.round,
    unit: "",
    zeroInherits: true,
    fmt: (n) => String(n || 10),
  },
  {
    key: "storageQuotaBytes",
    label: "Storage quota bytes",
    toUi: (n) => n,
    toWire: Math.round,
    unit: "",
    zeroInherits: true,
    fmt: (n) => (n === 0 ? "unlimited" : String(n)),
  },
  {
    key: "mailDailyBudget",
    label: "Report mails per day",
    toUi: (n) => n,
    toWire: Math.round,
    unit: "",
    zeroInherits: true,
    fmt: (n) => String(n || 100),
  },
];

/**
 * globalOf is the global value a dimension inherits. The report mail budget
 * has no global setting, so it reads 0, which its fmt spells as the built-in
 * value.
 */
function globalOf(limits: GlobalLimits, key: keyof TeamLimitOverrides): number {
  return key in limits ? limits[key as keyof GlobalLimits] : 0;
}

/**
 * page-admin-teams ("Team limits", route /admin/teams, operators only) manages
 * per-team resource overrides on top of the global limits. The table shows each
 * override, or a muted em-dash for inherited dimensions with the effective
 * value in the hover title. Adding an override searches teams server-side
 * (operators are not members of every team, so the regular /v1/teams listing
 * would not do) with a 300ms debounce; the ten inputs treat an empty string as
 * "inherit" (null on the wire) and their placeholders show the current global
 * value they would inherit.
 */
@customElement("page-admin-teams")
export class PageAdminTeams extends LitElement {
  @state() private rows?: TeamLimitRow[];
  @state() private limits?: GlobalLimits;
  @state() private loading = true;
  @state() private error = "";

  // Add/Edit modal: the chosen team (fixed when editing), the debounced team
  // search, and the ten override inputs ("" = inherit).
  @state() private modalOpen = false;
  @state() private team: AdminTeam | null = null;
  @state() private editing = false;
  @state() private searchQ = "";
  @state() private hits: AdminTeam[] = [];
  @state() private searching = false;
  @state() private values: Record<string, string> = {};
  @state() private saveBusy = false;

  // Remove flow.
  @state() private removeTarget: TeamLimitRow | null = null;
  @state() private removing = false;

  private searchTimer?: number;
  private searchReq = 0;

  static override styles = [
    adminTabsStyles,
    css`
      :host {
        display: block;
      }
      .head {
        margin-bottom: var(--bino-space-lg);
      }
      h1 {
        margin: 0;
        font-size: var(--bino-font-size-xl);
        font-weight: 600;
        color: var(--bino-text);
      }
      .sub {
        margin-top: var(--bino-space-xs);
        font-size: var(--bino-font-size-md);
        color: var(--bino-text-secondary);
      }
      .inherit {
        color: var(--bino-text-secondary);
      }
      .row-actions {
        display: inline-flex;
        gap: var(--bino-space-xs);
      }
      .search-results {
        display: flex;
        flex-direction: column;
        margin-top: var(--bino-space-xs);
        border: 1px solid var(--bino-border);
        border-radius: var(--bino-radius);
        overflow: hidden;
      }
      .search-hit {
        text-align: left;
        font-family: var(--bino-font-sans);
        font-size: var(--bino-font-size-md);
        color: var(--bino-text);
        background: var(--bino-surface);
        border: none;
        border-bottom: 1px solid var(--bino-border);
        padding: 0.5rem 0.6875rem;
        cursor: pointer;
      }
      .search-hit:last-child {
        border-bottom: none;
      }
      .search-hit:hover {
        background: var(--bino-surface-hover);
      }
      .search-empty {
        font-family: var(--bino-font-sans);
        font-size: var(--bino-font-size-sm);
        color: var(--bino-text-secondary);
        padding: 0.5rem 0.6875rem;
      }
      .chip {
        display: inline-flex;
        align-items: center;
        gap: var(--bino-space-xs);
        padding: 0.25rem 0.375rem 0.25rem 0.625rem;
        border-radius: var(--bino-radius-pill);
        background: var(--bino-surface-active);
        color: var(--bino-active-text);
        font-family: var(--bino-font-sans);
        font-size: var(--bino-font-size-md);
        font-weight: 600;
      }
      .chip button {
        background: none;
        border: none;
        cursor: pointer;
        color: inherit;
        font-size: 14px;
        line-height: 1;
        padding: 2px;
      }
      .override-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 0 var(--bino-space-md);
      }
      input[type="number"] {
        width: 100%;
        box-sizing: border-box;
        font-family: var(--bino-font-sans);
        font-size: var(--bino-font-size-md);
        color: var(--bino-text);
        padding: 0.5rem 0.6875rem;
        border: 1px solid var(--bino-border-light);
        border-radius: var(--bino-radius);
        background: var(--bino-surface-subtle);
      }
      input[type="number"]:focus {
        outline: none;
        border-color: var(--bino-primary);
        box-shadow: 0 0 0 3px var(--bino-focus-ring);
      }
      input[type="number"]::placeholder {
        color: var(--bino-text-secondary);
      }
      .modal-copy {
        margin: 0 0 var(--bino-space-md);
        font-family: var(--bino-font-sans);
        font-size: var(--bino-font-size-base);
        color: var(--bino-text-secondary);
      }
      .confirm-name {
        font-family: var(--bino-font-mono);
        color: var(--bino-text);
      }
    `,
  ];

  override connectedCallback() {
    super.connectedCallback();
    void this.load();
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    if (this.searchTimer !== undefined) window.clearTimeout(this.searchTimer);
  }

  private async load() {
    this.loading = true;
    this.error = "";
    try {
      const [rows, settings] = await Promise.all([listTeamLimits(), getSettings()]);
      this.rows = rows;
      this.limits = settings.limits;
    } catch (e) {
      this.error = humanError(e);
    } finally {
      this.loading = false;
    }
  }

  // ---- Add / Edit --------------------------------------------------------------

  private openAdd() {
    this.team = null;
    this.editing = false;
    this.searchQ = "";
    this.hits = [];
    this.values = {};
    this.modalOpen = true;
  }

  private openEdit(row: TeamLimitRow) {
    this.team = { id: row.teamId, name: row.teamName };
    this.editing = true;
    const values: Record<string, string> = {};
    for (const dim of DIMS) {
      const v = row.limits[dim.key];
      values[dim.key] = v === null ? "" : String(dim.toUi(v));
    }
    this.values = values;
    this.modalOpen = true;
  }

  private closeModal() {
    if (this.saveBusy) return;
    this.modalOpen = false;
  }

  private onSearchInput(q: string) {
    this.searchQ = q;
    if (this.searchTimer !== undefined) window.clearTimeout(this.searchTimer);
    if (q.trim() === "") {
      this.hits = [];
      this.searching = false;
      return;
    }
    this.searching = true;
    this.searchTimer = window.setTimeout(() => void this.runSearch(q.trim()), SEARCH_DEBOUNCE_MS);
  }

  private async runSearch(q: string) {
    const id = ++this.searchReq;
    try {
      const hits = await searchTeams(q);
      if (id !== this.searchReq) return;
      this.hits = hits;
    } catch (e) {
      if (id !== this.searchReq) return;
      toast(humanError(e), "error");
    } finally {
      if (id === this.searchReq) this.searching = false;
    }
  }

  private pickTeam(t: AdminTeam) {
    this.team = t;
    this.searchQ = "";
    this.hits = [];
  }

  /**
   * parsedOverrides converts the form to wire overrides. "" means inherit
   * (null), and so does 0 on the repository limits; anything else must be a
   * number > 0 — a non-positive or garbled field makes the whole form
   * unsubmittable (returns undefined). Every dimension is sent, null or not.
   */
  private parsedOverrides(): TeamLimitOverrides | undefined {
    const out = {} as Record<keyof TeamLimitOverrides, number | null>;
    for (const dim of DIMS) {
      const s = (this.values[dim.key] ?? "").trim();
      const n = Number(s);
      if (s === "" || (dim.zeroInherits && n === 0)) {
        out[dim.key] = null;
        continue;
      }
      if (!Number.isFinite(n) || n <= 0) return undefined;
      out[dim.key] = dim.toWire(n);
    }
    return out as TeamLimitOverrides;
  }

  private async saveOverride() {
    const overrides = this.parsedOverrides();
    if (!this.team || !overrides || this.saveBusy) return;
    this.saveBusy = true;
    try {
      await upsertTeamLimits(this.team.id, overrides);
      toast(`Limits for ${this.team.name} saved`, "success");
      this.modalOpen = false;
      void this.load();
    } catch (e) {
      toast(humanError(e), "error");
    } finally {
      this.saveBusy = false;
    }
  }

  // ---- Remove ------------------------------------------------------------------

  private async confirmRemove() {
    const target = this.removeTarget;
    if (!target || this.removing) return;
    this.removing = true;
    try {
      await deleteTeamLimits(target.teamId);
      toast(`Override for ${target.teamName} removed`, "success");
      this.removeTarget = null;
      void this.load();
    } catch (e) {
      toast(humanError(e), "error");
    } finally {
      this.removing = false;
    }
  }

  // ---- Render ------------------------------------------------------------------

  /** An override cell: the value, or a muted em-dash titled with the effective value. */
  private dimCell(row: TeamLimitRow, dim: Dim) {
    const v = row.limits[dim.key];
    if (v === null) {
      const eff = row.effective[dim.key];
      return html`<span class="inherit" title=${`Inherits ${show(dim, eff)} from the global limits`}
        >—</span
      >`;
    }
    return html`${show(dim, v)}`;
  }

  private tableRows(rows: TeamLimitRow[]): BnRow[] {
    return rows.map((row) => {
      const cells: BnRow = { team: row.teamName || row.teamId };
      for (const dim of DIMS) cells[dim.key] = this.dimCell(row, dim);
      cells.actions = html`<span class="row-actions">
        <bn-button size="sm" variant="secondary" @click=${() => this.openEdit(row)}>Edit</bn-button>
        <bn-button
          size="sm"
          variant="danger"
          @click=${() => (this.removeTarget = row)}
          aria-label=${"Remove override for " + row.teamName}
          >Remove</bn-button
        >
      </span>`;
      return cells;
    });
  }

  private renderTable() {
    const rows = this.rows ?? [];
    if (rows.length === 0) {
      return html`<bn-card flush>
        <bn-empty-state
          icon="⚖"
          heading="No team overrides"
          message="Every team inherits the global limits. Add an override to give one team different resources."
        >
          <bn-button slot="actions" variant="primary" @click=${() => this.openAdd()}
            >Add override</bn-button
          >
        </bn-empty-state>
      </bn-card>`;
    }
    const columns: BnColumn[] = [
      { key: "team", label: "Team" },
      ...DIMS.map((d): BnColumn => ({ key: d.key, label: d.label, align: "right", mono: true })),
      { key: "actions", label: "", align: "right", width: "1px" },
    ];
    return html`<bn-card flush heading="Team overrides">
      <bn-button slot="actions" variant="primary" size="sm" @click=${() => this.openAdd()}
        >Add override</bn-button
      >
      <bn-table .columns=${columns} .rows=${this.tableRows(rows)}></bn-table>
    </bn-card>`;
  }

  private renderTeamPicker() {
    if (this.team) {
      return html`<bn-field label="Team" required>
        <span class="chip">
          ${this.team.name}
          ${this.editing
            ? nothing
            : html`<button
                title="Choose a different team"
                aria-label="Clear selected team"
                @click=${() => (this.team = null)}
              >
                ×
              </button>`}
        </span>
      </bn-field>`;
    }
    return html`<bn-field label="Team" required hint="Search by team name.">
      <bn-input
        placeholder="acme"
        autocomplete="off"
        .value=${this.searchQ}
        @input=${(e: CustomEvent) => this.onSearchInput((e.detail as { value: string }).value)}
      ></bn-input>
      ${this.searchQ.trim() !== ""
        ? html`<div class="search-results">
            ${this.hits.map(
              (t) => html`<button class="search-hit" @click=${() => this.pickTeam(t)}>${t.name}</button>`,
            )}
            ${this.hits.length === 0
              ? html`<div class="search-empty">${this.searching ? "Searching…" : "No teams match."}</div>`
              : nothing}
          </div>`
        : nothing}
    </bn-field>`;
  }

  private renderModal() {
    const limits = this.limits;
    return html`<bn-modal
      ?open=${this.modalOpen}
      width=${560}
      heading=${this.editing ? "Edit team override" : "Add team override"}
      @close=${() => this.closeModal()}
    >
      <p class="modal-copy">
        Leave a field empty to inherit the global value (shown as the placeholder). Container overrides
        apply to new containers only.
      </p>
      ${this.renderTeamPicker()}
      <div class="override-grid">
        ${DIMS.map((dim) => {
          const globalValue = limits === undefined ? "" : show(dim, globalOf(limits, dim.key));
          const label =
            dim.label +
            (dim.unit ? ` (${dim.unit.trim()})` : "") +
            (dim.zeroInherits ? " (0 = inherit)" : "");
          return html`<bn-field label=${label}>
            <input
              type="number"
              step=${dim.key === "cpuCores" ? "0.25" : "1"}
              min="0"
              aria-label=${dim.label}
              placeholder=${globalValue}
              .value=${this.values[dim.key] ?? ""}
              @input=${(e: Event) =>
                (this.values = {
                  ...this.values,
                  [dim.key]: (e.target as HTMLInputElement).value,
                })}
            />
          </bn-field>`;
        })}
      </div>
      <bn-button slot="footer" variant="ghost" @click=${() => this.closeModal()}>Cancel</bn-button>
      <bn-button
        slot="footer"
        variant="primary"
        ?loading=${this.saveBusy}
        ?disabled=${this.team === null || this.parsedOverrides() === undefined}
        @click=${() => void this.saveOverride()}
        >${this.editing ? "Save override" : "Add override"}</bn-button
      >
    </bn-modal>`;
  }

  private renderRemoveModal() {
    const target = this.removeTarget;
    return html`<bn-modal
      ?open=${target !== null}
      heading="Remove override?"
      @close=${() => {
        if (!this.removing) this.removeTarget = null;
      }}
    >
      ${target
        ? html`<p>
            <span class="confirm-name">${target.teamName}</span> will go back to inheriting the
            global limits for all dimensions. New containers pick this up immediately.
          </p>`
        : nothing}
      <bn-button
        slot="footer"
        variant="ghost"
        @click=${() => {
          if (!this.removing) this.removeTarget = null;
        }}
        >Cancel</bn-button
      >
      <bn-button
        slot="footer"
        variant="danger"
        ?loading=${this.removing}
        @click=${() => void this.confirmRemove()}
        >Remove override</bn-button
      >
    </bn-modal>`;
  }

  private renderBody() {
    if (this.loading && !this.rows) {
      return html`<bn-spinner block label="Loading team limits…"></bn-spinner>`;
    }
    if (this.error && !this.rows) {
      return html`<bn-empty-state icon="⚠" heading="Couldn't load team limits" message=${this.error}>
        <bn-button slot="actions" variant="primary" @click=${() => void this.load()}>Retry</bn-button>
      </bn-empty-state>`;
    }
    return this.renderTable();
  }

  override render() {
    return html`
      <div class="head">
        <h1>Admin</h1>
        <div class="sub">Per-team resource overrides on top of the global limits.</div>
      </div>
      ${adminTabs("teams")} ${this.renderBody()} ${this.renderModal()} ${this.renderRemoveModal()}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "page-admin-teams": PageAdminTeams;
  }
}
